import { TEXTOS } from '../../lib/glossario'
import type { EstadoDoResumo, SetorDoAutor } from '../../types/repositorios'

/** A ordem em que os setores aparecem: quem o contrato mede primeiro; robô por último. */
export const ORDEM_DOS_SETORES: SetorDoAutor[] = ['CONTRATADA', 'SMF', 'SEXEC', 'PREFEITURA', 'SEM_VINCULO', 'AUTOMACAO']

/** A cor da etiqueta de cada setor. Preenchimento claro com texto escuro: passa em AA como letra. */
export const CLASSE_DO_SETOR: Record<SetorDoAutor, string> = {
  CONTRATADA: 'bg-laranja-50 text-laranja-900 ring-laranja-200',
  SMF: 'bg-sky-50 text-sky-900 ring-sky-200',
  SEXEC: 'bg-niteroi-50 text-niteroi-900 ring-niteroi-200',
  PREFEITURA: 'bg-teal-50 text-teal-900 ring-teal-200',
  SEM_VINCULO: 'bg-amber-50 text-amber-900 ring-amber-200',
  AUTOMACAO: 'bg-slate-100 text-slate-700 ring-slate-200',
}

/**
 * O rótulo de um setor, tolerante a código desconhecido: o texto da IA pode escrever o setor de um
 * jeito que o vocabulário não previu, e a tela mostra o código em vez de quebrar.
 */
export function rotuloDoSetor(setor: string): string {
  return TEXTOS.repositorios.setores[setor]?.rotulo ?? setor
}

/** A ajuda de um setor, quando o código é conhecido. */
export function ajudaDoSetor(setor: string): string | undefined {
  return TEXTOS.repositorios.setores[setor]?.ajuda
}

/** O rótulo do estado do resumo. */
export function rotuloDoEstado(estado: EstadoDoResumo): string {
  return TEXTOS.repositorios.resumo.estados[estado]
}

/** O mês corrente no fuso local, no formato `AAAA-MM`. */
export function competenciaAtual(hoje: Date = new Date()): string {
  return `${hoje.getFullYear()}-${String(hoje.getMonth() + 1).padStart(2, '0')}`
}

/**
 * O mês anterior ou seguinte a uma competência, sem passar por `Date`: aritmética de inteiros não
 * tem fuso, e é o fuso que faz "1º de setembro" virar "31 de agosto" quando alguém converte.
 */
export function mesVizinho(competencia: string, passo: -1 | 1): string {
  const [ano, mes] = competencia.split('-').map(Number)
  if (!ano || !mes) {
    return competencia
  }
  const total = ano * 12 + (mes - 1) + passo
  const novoAno = Math.floor(total / 12)
  const novoMes = (total % 12) + 1
  return `${novoAno}-${String(novoMes).padStart(2, '0')}`
}

/** Os meses que o seletor oferece: os que têm dado mais o corrente, do mais recente para o mais antigo. */
export function mesesDoSeletor(comDado: string[], atual: string): string[] {
  return Array.from(new Set([atual, ...comDado])).sort((a, b) => (a < b ? 1 : a > b ? -1 : 0))
}

/** Uma competência é válida quando tem a forma `AAAA-MM` com mês entre 1 e 12. */
export function competenciaValida(texto: string): boolean {
  const partes = /^(\d{4})-(\d{2})$/.exec(texto)
  if (!partes) {
    return false
  }
  const mes = Number(partes[2])
  return mes >= 1 && mes <= 12
}
